import React, { useState } from 'react';
import { Brain, MessageSquare, Zap, Users, BarChart3 } from 'lucide-react';
import AIModeIndicator from './AIModeIndicator';
import { AI_MODE } from '../../constants/aiAssistant';

/**
 * Canonical tab IDs — same across all RCM services.
 * Use these in renderContent(activeTab) so UI and backend stay aligned.
 */
export const KAI_TAB_IDS = {
  ASK_KAI: 'ask-kai',
  PROCESSING: 'processing',
  HUMAN_REVIEW: 'human-review',
  ANALYTICS: 'analytics',
};

/** Default tabs: same order and labels for every service */
const DEFAULT_TABS = [
  { id: KAI_TAB_IDS.ASK_KAI, label: 'Ask Kai', icon: MessageSquare },
  { id: KAI_TAB_IDS.PROCESSING, label: 'Processing', icon: Zap },
  { id: KAI_TAB_IDS.HUMAN_REVIEW, label: 'Human Review', icon: Users },
  { id: KAI_TAB_IDS.ANALYTICS, label: 'Analytics', icon: BarChart3 },
];

/**
 * Unified Kai page layout — same UI design for every RCM service.
 * One route (/kai); mode (Copilot vs CoWorker) comes from settings.
 *
 * @param {string} serviceName - e.g. "Prior Authorization", "Insurance Verification"
 * @param {string} mode - AI_MODE.COPILOT | AI_MODE.COWORKER (from settings)
 * @param {string} [subtitle] - Optional one-line description
 * @param {Array<{id, label, icon}>} [tabs] - Override tabs; default is Ask Kai, Processing, Human Review, Analytics
 * @param {function(activeTab: string): ReactNode} renderContent - Renders content for the active tab id
 * @param {string} [defaultTab] - Initial tab id (default: ask-kai)
 */
const KaiPageLayout = ({
  serviceName,
  mode = AI_MODE.COPILOT,
  subtitle,
  tabs = DEFAULT_TABS,
  renderContent,
  defaultTab = KAI_TAB_IDS.ASK_KAI,
}) => {
  const [activeTab, setActiveTab] = useState(defaultTab);

  return (
    <div className="space-y-6">
      {/* Header — same structure everywhere */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg">
              <Brain className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Kai – {serviceName}
              </h1>
              <p className="text-gray-600 mt-0.5">
                {subtitle || 'Information & Education, plus suggestions and processing'}
              </p>
              <p className="text-sm text-blue-600 font-medium mt-1" aria-label="Kai role">
                Information & Education — Kai helps you learn and work smarter in this service.
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <AIModeIndicator mode={mode} serviceName={serviceName} />
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-sm text-gray-600">Kai Online</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs — same pattern everywhere */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" role="tablist">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>
        <div className="p-6">
          {renderContent ? renderContent(activeTab) : null}
        </div>
      </div>
    </div>
  );
};

export default KaiPageLayout;
export { DEFAULT_TABS };
